import React, { memo } from 'react';
import { Flex } from 'antd-mobile';
import { useObserver } from 'mobx-react';
import PropTypes from 'prop-types';
import { ImgWrap } from 'pubBiz';
import { ClassifyRecommandCell } from 'pubBiz/CommonCell';
import cx from 'classnames';
import styles from './index.modules.less';
import Util from 'utils';

const defaultProps = {
  data: {},
  onPress: null
};

// 商品信息数据结构
const itemInfoPropTypes = PropTypes.shape({
  id: PropTypes.string, // 商品ID
  itemId: PropTypes.string, // 商品ID（兼容字段）
  imgUrl: PropTypes.string, // 商品图片
  name: PropTypes.string, // 商品名称
  price: PropTypes.number, // 商品价格
  maxPrice: PropTypes.number, // 商品最高价格
  type: PropTypes.number // 商品类型（6：多规格商品）
});

// 卖点数据结构
const sellPointPropTypes = PropTypes.shape({
  sellPointColour: PropTypes.string, // 卖点颜色（支持'FF0000'或'#FF0000'格式）
  sellPointName: PropTypes.string // 卖点文案
});

// classifyItemRelationRespDtoList成员结构
const classifyItemRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    itemInfo: itemInfoPropTypes, // 商品信息
    sellPointList: PropTypes.arrayOf(sellPointPropTypes) // 卖点列表
  })
);

// classifyLinkRelationRespDtoList成员结构
const classifyLinkRelationRespDtoPropTypes = PropTypes.arrayOf(
  PropTypes.shape({
    id: PropTypes.string, // 推荐位ID
    type: PropTypes.number, // 推荐位类型
    content: PropTypes.string, // 推荐位内容
    extend: PropTypes.object, // 扩展字段
    imgUrlApp: PropTypes.string // 推荐位图片
  })
);

// data成员结构
const dataPropTypes = PropTypes.shape({
  mainImgUrl: PropTypes.string, // 背景图
  title: PropTypes.string, // 主标题
  titleColor: PropTypes.string, // 标题颜色
  moreText: PropTypes.string, // 进入标识
  moreColor: PropTypes.string, // 进入标识颜色
  filletValue: PropTypes.number, // 圆角值
  relativeType: PropTypes.number, // 内容目录 0: 产品目录 1: 推荐链接目录
  classifyItemRelationRespDtoList: classifyItemRelationRespDtoPropTypes, // 商品列表
  classifyLinkRelationRespDtoList: classifyLinkRelationRespDtoPropTypes // 推荐位列表
});

const propTypes = {
  width: PropTypes.number.isRequired, // 容器宽度
  data: dataPropTypes,
  onPress: PropTypes.func // 点击事件
};

const useAppModData = () => {
  const { token, userId, userInfo } = Util.useStores('AppMod');
  return useObserver(() => ({
    token,
    userId,
    userInfo
  }));
};

const Bg_6Grid = memo((props) => {
  const {
    width: cellWidth, // Any 容器宽度
    data, // Object 数据
    onPress // Function 点击事件
  } = props;

  const {
    dynamicEffect = 1, // Number 显示动效 1: 固定展示 2: 自动轮播
    mainImgUrl, // String 背景图
    title, // String 主标题
    titleColor, // String 标题颜色
    moreText, // String 进入标识
    moreColor, // String 进入标识颜色
    filletValue = 0, // Number 圆角值
    relativeType, // Number 内容目录 0: 产品目录 1: 推荐链接目录
    classifyLinkRelationRespDtoList // Array 推荐位
  } = data;

  const { token, userId, userInfo } = useAppModData();

  const showDynamicEffect = dynamicEffect == 2;

  // 推荐位列表
  const renderRecommands = () => {
    // 推荐位图片的尺寸为 105 * 85
    // Todos: classifyLinkRelationRespDtoList filter 掉 displayMode === 1 是后端未过滤小程序展示内容做的保底，当后端修复这个缺陷后可以去掉该逻辑

    return (
      <Flex justify="center" className={styles.recommandList}>
        <ClassifyRecommandCell
          autoplay={showDynamicEffect}
          infinite={showDynamicEffect}
          autoplayIntervalProp={4000}
          width={`calc(${cellWidth} - 0.48rem)`}
          data={{
            itemList: (classifyLinkRelationRespDtoList || []).filter((o) => !(o.displayMode === 1))
          }}
          config={{
            radius: '0.08rem'
          }}
          vertical
          verticalConfig={{
            size: {
              width: '2.1rem',
              height: '1.7rem'
            },
            count: 3
          }}
          onPress={(val) => {
            const { type, content, specialDescription } = val;
            if (type == 6) {
              window.sensors.track('userlifeCycle', {
                information_title: '六宫格-活动-' + content,
                type: 1, //type 1,2,3,4 区分点击、登录、注册、下单
                channal: 'H5',
                isLogin: !!token,
                userId: userId || ''
              });
            } else if (type == 8) {
              // 六宫格配专题页埋点
              window.sensors.track('userlifeCycle', {
                information_title: '六宫格-' + (specialDescription || content),
                type: 1, //type 1,2,3,4 区分点击、登录、注册、下单
                channal: 'H5',
                isLogin: !!token,
                userId: userId,
                cardCode: userInfo && userInfo.cardCode
              });
            } else if (type == 10 && content) {
              // 六宫格配内部链接埋点
              const contentObj = JSON.parse(content);
              const route = contentObj.route || contentObj.appLink;
              if (route === 'YofotoBeanMall') {
                window.sensors.track('userlifeCycle', {
                  information_title: '六宫格-' + (specialDescription || content),
                  type: 1, //type 1,2,3,4 区分点击、登录、注册、下单
                  channal: 'H5',
                  isLogin: !!token,
                  userId: userId || '',
                  cardCode: userInfo && userInfo.cardCode
                });
              }
              window.sensors.track('functionClickCount', {
                authCode: '', //权限点名称
                name: '生命周期--' + specialDescription, //功能名字
                belongSystem: userInfo && userInfo.belongSystem, //站点
                position: '首页', //位置
                source: 'H5' //来源
              });
            }

            onPress && onPress(val);
          }}
        />
      </Flex>
    );
  };

  const renderHeader = () => {
    return (
      <Flex
        direction="row"
        align="center"
        justify="between"
        className={styles.headerContainer}
        onClick={() => onPress && onPress(data)}>
        <div className={styles.title} style={{ color: titleColor }}>
          <span>{title || ''}</span>
        </div>
        {!!moreText && (
          <div className={styles.more} style={{ color: moreColor }}>
            <span>{`${moreText || ''}`}</span>
            <span
              className={cx('iconfont', 'icon-arrow-right', styles.arrowRightIcon)}
              style={{ color: moreColor }}></span>
          </div>
        )}
      </Flex>
    );
  };

  const renderContent = () => {
    return renderRecommands();
  };

  // 不支持推荐产品，仅支持推荐位
  if (relativeType != 1) return null;

  return (
    // 背景图尺寸 702*478
    <Flex
      className={styles.wrapper}
      style={{
        borderRadius: `${(filletValue * 2) / 100}rem`
      }}>
      <ImgWrap w={cellWidth} h={`calc(${cellWidth} * 478 / 702)`} src={mainImgUrl || ''} />
      <Flex direction="column" align="start" className={styles.container}>
        {renderHeader()}
        {renderContent()}
      </Flex>
    </Flex>
  );
});

Bg_6Grid.defaultProps = defaultProps;
Bg_6Grid.propTypes = propTypes;

export default Bg_6Grid;



// WEBPACK FOOTER //
// ./src/pubBiz/CommonCell/ClassifyCell/components/BG_6Grid/index.js